import * as _ from 'lodash'
import { AfterViewInit, Component, effect, OnInit, output, OutputEmitterRef, signal, TemplateRef, viewChild } from '@angular/core'

import { IImageParentData } from 'src/app/features/image/models/image-parent-data'
import { ILevelGeneralData } from '../../models/level-general-data'
import { _SectionFormComponent } from './_section-form-component'
import { ILevelGeneralDto, levelGeneralDto } from '../../models/level-general-dto'
import { catchError, defer, EMPTY, filter, finalize, Observable, of, Subscription, tap } from 'rxjs'
import { HttpParams } from '@angular/common/http'
import { pageMode, TPageMode } from '../../models/page-mode'
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data'
import { LogService } from '../../services/log.service'
import { TRoutePage } from '../../services/route-service/models/route-page'
import { routeActionParam } from '../../services/page-services/models/return-page-query-param'

@Component({
    selector: 'app-edit-section',
    template: '',
    styles: []
})
export abstract class _SectionEditComponent<DtoModel extends ILevelGeneralDto, DataModel extends ILevelGeneralData, FormModel>
    extends _SectionFormComponent<DataModel>
    implements AfterViewInit, OnInit
{
    private __onCancelEmitSubscr?: Subscription;
    private __onSaveEmitSubscr?: Subscription;

    protected abstract _create_post$(dto: any, httpParams: HttpParams | null, requestOptions: any): Observable<DtoModel>;
    protected abstract _edit_patch$(dto: any, httpParams: HttpParams, requestOptions: any): Observable<DtoModel>;

    abstract out_onCancel: OutputEmitterRef<void>
    abstract out_onEscape: OutputEmitterRef<void>
    abstract out_onSave: OutputEmitterRef<void>

    abstract emit_onCancel(): void;
    abstract emit_onEscape(): void;
    abstract emit_onSave(): void;
    abstract ngOnDestroy(): void;
    abstract ngOnInit(): void;

    abstract onCreate_httpParams: HttpParams | null
    abstract onCreate_requestOptions: any
    abstract onEdit_httpParams: HttpParams
    abstract onEdit_requestOptions: any

    private __initialize = true
    private __buttons$$ = viewChild<TemplateRef<any>>('buttons')

    protected _out_onCancel = output({alias:'onCancel'})
    protected _out_onEscape = output({alias:'onEscape'})
    protected _out_onSave = output({alias:'onSave'})

    // Lopende startrequests waarvan het resultaat zichtbaar is in Edit (bv. de opties van een select): pas als ze klaar
    // zijn telt de pagina als geladen en toont section-details de Edit (zie _CentralPageService.sectionEditReady$$).
    protected readonly _loadsPending$$ = signal(0)
    private __loaded_dataModel: DataModel | null = null

    protected override _footerData_section: Partial<IFooterData> = {hideSideBlockLeft: false}

    constructor() {
        super()

        effect(() => this._centralPageService.sectionEdit_extraButtons_template_set(this.__buttons$$()))
        effect(() => {
            const _dataModel = this.dataModel$$()

            // Eén keer per model, en pas als de startrequests (_load_track) klaar zijn.
            if (!!_dataModel && this._loadsPending$$() === 0 && _dataModel !== this.__loaded_dataModel) {
                this.__loaded_dataModel = _dataModel
                this._emit_pageLoaded()
                setTimeout(() => {
                    if (this._loadsPending$$() === 0) this._centralPageService.sectionEditReady_set(true)
                }, 0)
            }
        })
    }

    // Startrequest laten meetellen voor 'geladen' (zie _loadsPending$$). Telt bij subscribe, aftellen bij afronden of fout.
    protected _load_track<T>(source$: Observable<T>): Observable<T> {
        return defer(() => {
            this._loadsPending$$.update(n => n + 1)
            return source$.pipe(finalize(() => this._loadsPending$$.update(n => n - 1)))
        })
    }

    protected override _ngOnInit(): void {
        // const _footer_page = this._pageService.footerData_page$$();
        const _header_page = this._pageService.headerData_page$$();
        const _currentPage = this._pageService.pageCurrent$$();
        const _defaultName = `edit ${_currentPage?.displayName}`;

        this._headerData_section = {title: _header_page?.title ?? _defaultName};
        // this._footerData_section = {hideSideBlockLeft: _footer_page?.hideSideBlockLeft ?? false};

        // Edit-sectie levert de autorisatiepagina voor de save-knop (edit-slot). In create-modus
        // resolvet het CLAIM_PAGE-token via skipSelf naar de create-page van de create-container
        // (zie de useFactory-provider op de concrete *EditComponent), dus deze push klopt in beide
        // gevallen — geen aparte create-afhandeling meer nodig.
        if (this._claimPage) this._centralPageService.pageName_sectionEdit_set(this._claimPage);

        super._ngOnInit();

        this.__onCancelEmitSubscr = this._centralPageService.onCancelEmit$
            .pipe(
                filter(_ => !this.__initialize),
                tap(_ => this.emit_onCancel())
            )
            .subscribe();
        this.__onSaveEmitSubscr = this._centralPageService.onSaveEmit$
            .pipe(
                filter(_ => !this.__initialize),
                tap(_ => this.emit_onSave())
            )
            .subscribe();

        this.__initialize = false;
    }

    private __form_create$(httpParams?: HttpParams, requestOptions?: any): Observable<DtoModel> {
         const __form = this._form$$?.();
        if (!__form?.dirty) {return of()}

        const __formModel = __form.value;
        const __modelData = this._formModelService.dataModel$$();
        const __newModelData = this._formModelService.dataModel_alter(__modelData, __formModel);
        const __newModelDto = this._formModelService.dtoModel_alter(__newModelData);
        const __httpParams = httpParams ?? this.onCreate_httpParams;
        const __requestOptions = requestOptions ?? this.onCreate_requestOptions;

        return this._create_post$(__newModelDto, __httpParams, __requestOptions);
    }

    private __form_edit$(httpParams?: HttpParams, requestOptions?: any): Observable<DtoModel> {
        const __form = this._form$$?.();
        if (!__form?.dirty) {return of()}

        const __formModel = __form.value;
        const __modelData = this._formModelService.dataModel$$();
        const __newModelData = this._formModelService.dataModel_alter(__modelData, __formModel);
        const __newModelDto = this._formModelService.dtoModel_alter(__newModelData);
        const __httpParams = httpParams ?? this.onEdit_httpParams;
        const __requestOptions = requestOptions ?? this.onEdit_requestOptions;

        return this._edit_patch$(__newModelDto, __httpParams, __requestOptions).pipe(
            tap(modelDto => {
                this._formModelService.dtoModel_set(modelDto)
            })
        );
    }

    protected _emit_onCancel(): void {
        setTimeout(() => {
            const __pageMode = this._centralPageService.pageMode$$()

            if (__pageMode == pageMode.editPage) {
                this._centralPageService.pageMode_set(this.__pageMode_afterEdit())
            }
        }, 0);
        this._out_onCancel.emit()
    }

    protected _emit_onEnter(): void {
        const __form = this._formModelService.form;

        // Zelfde voorwaarde als de disabled-state van de Save-knop (form.component.ts): niet
        // opslaan bij een ongewijzigd of ongeldig formulier — Enter mag de knop niet omzeilen.
        if (!__form?.dirty || __form.invalid) {return}

        super._emit_onEnter()
        this.emit_onSave()
    }

    protected _emit_onEscape(): void {
        this._out_onEscape.emit();
    }

    protected _emit_onSave(): void {
        this._out_onSave.emit();
    }

    protected _save(pageNameDetails: TRoutePage): void {
        this._save$(pageNameDetails)
        .pipe(
            tap(_ => this._emit_onSave()),
            // _edit_patch$/_create_post$ gebruiken unwrapData(), dat bij een backend-fout
            // gooit i.p.v. als succes doorstroomt. De notificatie is dan al getoond door de
            // ApiService-middleware; hier alleen voorkomen dat pageMode/dtoModel alsnog naar
            // 'succes' schakelen — form/pagina blijft staan zodat de gebruiker kan corrigeren.
            catchError(err => {
                LogService.envDev.error('[section-edit] save failed', err);
                return EMPTY;
            })
        )
        .subscribe();
    }

    protected _save$(pageNameDetails: TRoutePage): Observable<DtoModel> {
        const __pageMode = this._centralPageService.pageMode$$();

        if (__pageMode == pageMode.createPage) {
            return this.__form_create$().pipe(
                tap((modelDto: DtoModel) => {
                    const __routeKeys = {...this._activatedRouteService.routeKeys, key: modelDto[levelGeneralDto.guid]}
                    const __returnPath = this._pageService.PageReturn?.routePath ?? null;
                    const __routeParams = __returnPath ? {[routeActionParam.pageReturn]: __returnPath} : null;

                    this._pageService.page_goto(pageNameDetails, __routeKeys, null, __routeParams);
                })
        )
        } else {
            return this.__form_edit$().pipe(
                tap(_ => {
                    this._centralPageService.pageMode_set(this.__pageMode_afterEdit())
                })
            );
        };
    }

    // Na Save/Cancel terug naar de stand van vóór Edit (zelfde regel als Escape in _SectionFormComponent):
    // kwam je uit de tab-weergave, dan blijven de tabs met hun tab-footer (incl. Edit) staan en blijft de
    // footerbalk voor de tab; alleen zonder bekende vorige stand naar detailsPage.
    private __pageMode_afterEdit(): TPageMode {
        const __prev = this._centralPageService.pageMode_prev$$();
        return __prev && __prev !== pageMode.editPage && __prev !== pageMode.createPage ? __prev : pageMode.detailsPage;
    }

    protected _ngOnDestroy(): void {
        this.__onCancelEmitSubscr?.unsubscribe();
        this.__onSaveEmitSubscr?.unsubscribe();
    }
}
